import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getSnapshotCounts } from './environmentImportValidation.js';

const formatDate = (value) => {
  if (!value) return 'Non renseignée';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('fr-FR');
};

const getId = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object' && value._id !== undefined) return String(value._id);
  return String(value);
};

const EnvironmentImportSummary = ({ snapshot }) => {
  const manifest = snapshot?.manifest || {};
  const counts = getSnapshotCounts(snapshot);
  const competitions = Array.isArray(snapshot?.data?.competitions) ? snapshot.data.competitions : [];
  const teams = Array.isArray(snapshot?.data?.teams) ? snapshot.data.teams : [];
  const players = Array.isArray(snapshot?.data?.players) ? snapshot.data.players : [];
  const matches = Array.isArray(snapshot?.data?.matches) ? snapshot.data.matches : [];
  const gameFacts = Array.isArray(snapshot?.data?.gameFacts) ? snapshot.data.gameFacts : [];

  const competitionStats = useMemo(() => {
    const result = new Map();

    competitions.forEach((competition) => {
      const competitionId = getId(competition?._id);
      const competitionTeamIds = new Set(
        teams
          .filter((team) => (Array.isArray(team?.competitions) ? team.competitions : []).map(getId).includes(competitionId))
          .map((team) => getId(team?._id))
          .filter(Boolean)
      );

      const competitionMatches = matches.filter((match) => getId(match?.competition) === competitionId);
      const competitionMatchIds = new Set(competitionMatches.map((match) => getId(match?._id)).filter(Boolean));

      const competitionPlayerIds = new Set(
        players
          .filter((player) =>
            (Array.isArray(player?.teams) ? player.teams : [])
              .map(getId)
              .some((teamId) => competitionTeamIds.has(teamId))
          )
          .map((player) => getId(player?._id))
          .filter(Boolean)
      );

      result.set(competitionId, {
        teams: competitionTeamIds.size,
        players: competitionPlayerIds.size,
        matches: competitionMatches.length,
        gameFacts: gameFacts.filter((gameFact) => competitionMatchIds.has(getId(gameFact?.idMatch))).length,
      });
    });

    return result;
  }, [competitions, teams, players, matches, gameFacts]);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Récapitulatif du snapshot
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cette étape lit uniquement le JSON sélectionné. Aucune donnée n'est envoyée ni écrite en base.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Provider source</Typography>
          <Typography fontWeight={600}>{manifest.sourceProvider || '—'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Discipline</Typography>
          <Typography fontWeight={600}>{manifest.discipline || '—'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Version snapshot</Typography>
          <Typography fontWeight={600}>{manifest.version ?? '—'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Créé le</Typography>
          <Typography fontWeight={600}>{formatDate(manifest.createdAt)}</Typography>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Chip label={`${counts.competitions} compétition(s)`} />
        <Chip label={`${counts.teams} équipe(s)`} />
        <Chip label={`${counts.players} joueur(s)`} />
        <Chip label={`${counts.matches} match(s)`} />
        <Chip label={`${counts.gameFacts} gameFact(s)`} />
      </Stack>

      {(manifest.startDate || manifest.endDate) && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Périmètre Match</Typography>
          <Typography variant="body2" color="text.secondary">
            {manifest.startDate ? formatDate(manifest.startDate) : 'Début non limité'}
            {' → '}
            {manifest.endDate ? formatDate(manifest.endDate) : 'Fin non limitée'}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Compétitions détectées
        </Typography>

        {competitions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucune compétition dans le snapshot.</Typography>
        ) : (
          competitions.map((competition) => {
            const competitionId = getId(competition?._id);
            const stats = competitionStats.get(competitionId) || {};
            const title = competition?.name || competitionId || 'Compétition';

            return (
              <Accordion key={competitionId || title} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ width: '100%', pr: 2 }}>
                    <Typography fontWeight={600}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {competition?.season || 'Saison non renseignée'}
                      {competition?.thirdPartyEntityId ? ` — ${competition.thirdPartyEntityId}` : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                    <Chip size="small" label={`${stats.teams || 0} équipe(s)`} />
                    <Chip size="small" label={`${stats.players || 0} joueur(s)`} />
                    <Chip size="small" label={`${stats.matches || 0} match(s)`} />
                    <Chip size="small" label={`${stats.gameFacts || 0} gameFact(s)`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Mongo id source : {competitionId || '—'}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>
    </Stack>
  );
};

export default EnvironmentImportSummary;
